import createSdk, {
  REFRESH_TOKEN_KEY,
  SESSION_TOKEN_KEY,
} from "@descope/web-js-sdk";
import { CELLS, isRemoteCell, type Cell, type RemoteCell } from "./cells";

export class CellSignInRequiredError extends Error {
  readonly cellId: string;

  constructor(cellId: string) {
    super(`Sign in to the ${cellId} cell to continue`);
    this.name = "CellSignInRequiredError";
    this.cellId = cellId;
  }
}

/**
 * Prefix for the cell's tokens in localStorage, so a cell session never
 * overwrites the primary session's DS / DSR keys.
 */
export const cellStoragePrefix = (cellId: string) => `cell.${cellId}.`;

// autoRefresh stays off: its timer refreshes with the unprefixed DSR, which is
// the primary session's token, and the 401 it gets clears the cell's session.
// getCellSessionToken refreshes on demand instead.
const createCellSdk = (cell: RemoteCell) =>
  createSdk({
    projectId: cell.remote.descopeProjectId,
    persistTokens: true as const,
    autoRefresh: false,
    storeLastAuthenticatedUser: false,
    storagePrefix: cellStoragePrefix(cell.id),
  });

type CellSdk = ReturnType<typeof createCellSdk>;

const sdks = new Map<string, CellSdk>();
const pendingRefreshes = new Map<string, Promise<unknown>>();

function getCellSdk(cell: RemoteCell): CellSdk {
  let sdk = sdks.get(cell.id);
  if (!sdk) {
    sdk = createCellSdk(cell);
    sdks.set(cell.id, sdk);
  }
  return sdk;
}

// Read from localStorage directly: the SDK's getters check an unprefixed DS
// cookie first, which belongs to the primary session.
const readCellToken = (
  cell: RemoteCell,
  key: typeof SESSION_TOKEN_KEY | typeof REFRESH_TOKEN_KEY,
) => localStorage.getItem(`${cellStoragePrefix(cell.id)}${key}`) ?? "";

/**
 * Saves the tokens a cell's sign-in flow returned under the cell's prefix, in
 * case the flow itself was configured not to persist them.
 */
export function storeCellSession(
  cellId: string,
  sessionJwt: string | undefined,
  refreshJwt: string | undefined,
): void {
  const prefix = cellStoragePrefix(cellId);
  if (sessionJwt) {
    localStorage.setItem(`${prefix}${SESSION_TOKEN_KEY}`, sessionJwt);
  }
  if (refreshJwt) {
    localStorage.setItem(`${prefix}${REFRESH_TOKEN_KEY}`, refreshJwt);
  }
}

const isUsable = (sdk: CellSdk, token: string) =>
  !!token && !sdk.isJwtExpired(token);

function hasCellSession(cell: RemoteCell): boolean {
  return isUsable(getCellSdk(cell), readCellToken(cell, REFRESH_TOKEN_KEY));
}

export const needsCellSignIn = (cell: Cell): boolean =>
  isRemoteCell(cell) && !hasCellSession(cell);

export async function getCellSessionToken(cell: RemoteCell): Promise<string> {
  const sdk = getCellSdk(cell);

  if (
    !isUsable(sdk, readCellToken(cell, SESSION_TOKEN_KEY)) &&
    hasCellSession(cell)
  ) {
    let refresh = pendingRefreshes.get(cell.id);
    if (!refresh) {
      refresh = sdk
        .refresh(readCellToken(cell, REFRESH_TOKEN_KEY))
        .finally(() => {
          pendingRefreshes.delete(cell.id);
        });
      pendingRefreshes.set(cell.id, refresh);
    }
    await refresh;
  }

  const token = readCellToken(cell, SESSION_TOKEN_KEY);
  if (!isUsable(sdk, token)) {
    throw new CellSignInRequiredError(cell.id);
  }
  return token;
}

async function signOutOfCell(cell: RemoteCell): Promise<void> {
  const refreshToken = readCellToken(cell, REFRESH_TOKEN_KEY);
  try {
    if (refreshToken) {
      await getCellSdk(cell).logout(refreshToken);
    }
  } finally {
    for (const key of [SESSION_TOKEN_KEY, REFRESH_TOKEN_KEY, "DSI"]) {
      localStorage.removeItem(`${cellStoragePrefix(cell.id)}${key}`);
    }
  }
}

export async function signOutOfAllCells(): Promise<void> {
  await Promise.allSettled(CELLS.filter(isRemoteCell).map(signOutOfCell));
}

const isUnauthorized = (error: unknown) => {
  const status = (error as { response?: { status?: number } } | null)?.response
    ?.status;
  return status === 401 || status === 403;
};

/** A failure that signing in to the cell again would fix. */
export const isCellSignInError = (error: unknown): boolean =>
  error instanceof CellSignInRequiredError || isUnauthorized(error);

/** React Query retry policy for per-cell calls: never retry a sign-in error. */
export const cellAwareRetry = (failureCount: number, error: unknown) =>
  !isCellSignInError(error) && failureCount < 3;

/**
 * Retry and error handling for a query sent to each cell. Another cell gets
 * one retry, so its outage can't stall a page, and its failure is shown next
 * to the data rather than as a global toast.
 */
export const cellQueryPolicy = (cell: Cell) => ({
  retry: isRemoteCell(cell)
    ? (failureCount: number, error: unknown) =>
        failureCount < 1 && cellAwareRetry(failureCount, error)
    : cellAwareRetry,
  meta: { cellFailureShownInline: true },
});
